/**
 * 地上的房子：铺面、楼子、衙署、车铺、汤池、瓦舍、货栈、城楼、民居……
 *
 * 画法跟家里的器物是同一套笔（sketch.js 的抖线），配色也沿用同一盘，
 * 只是体量大一些：占两格高的时候屋顶照例会溢出到格子上方，
 * 看着才像一栋房子压在地上，而不是一块地板。
 */

import { PALETTE, sline, srect, spoly, fillPoly, hatch, stext, wob } from './sketch.js';
import { PROP_ART } from './props_art.js';

/** 房子的几样材料色 */
export const BUILDING_COLORS = {
  tile: '#a09a90',
  tileDark: '#827b70',
  plaster: '#f2ecdc',
  brick: '#cbb69a',
  earth: '#d8c5a2',
  timber: '#8c6239',
  dark: '#5b5147',
  paper: '#f8f4e8',
  cloth: '#d9705e',
  clothBlue: '#6d8fa8',
};

const C = BUILDING_COLORS;

/* ------------------------------ 零件 ------------------------------ */

function box(ctx, x, y, w, h, fill, seed, o = {}) {
  if (fill) fillPoly(ctx, [[x, y], [x + w, y], [x + w, y + h], [x, y + h]], fill);
  srect(ctx, x, y, w, h, { color: o.line || PALETTE.ink, width: o.width || 1.2, seed });
}

function tileLine(ctx, x1, y1, x2, y2, seed) {
  sline(ctx, x1, y1, x2, y2, { color: PALETTE.ink, width: 1, alpha: 0.45, seed, amp: 0.6 });
}

/** 悬山屋面：屋脊 + 瓦垄 + 檐口阴影 */
function gableRoof(ctx, cx, apexY, halfW, eaveY, seed, color = C.tile) {
  const eave = halfW + Math.max(6, halfW * 0.12);
  const pts = [[cx - eave, eaveY], [cx, apexY], [cx + eave, eaveY]];
  fillPoly(ctx, pts, color);
  spoly(ctx, pts, { color: PALETTE.ink, width: 1.4, seed });
  sline(ctx, cx - eave, eaveY, cx + eave, eaveY, { color: PALETTE.ink, width: 1.6, seed: seed + 7 });
  for (let i = 1; i < 6; i += 1) {
    tileLine(ctx, cx, apexY + 2, cx - eave + eave * 2 * (i / 6), eaveY - 1, seed + i * 11);
  }
  hatch(ctx, cx - eave, eaveY - 4, eave * 2, 4, { gap: 7, seed: seed + 3, alpha: 0.35 });
}

/** 重檐：上下两层屋面，楼阁与庙宇用 */
function stackRoof(ctx, cx, y, halfW, seed) {
  gableRoof(ctx, cx, y + 2, halfW * 0.86, y + 16, seed + 21);
  gableRoof(ctx, cx, y + 18, halfW, y + 34, seed + 41);
}

/** 单坡铁皮顶：车棚、车铺、堆场 */
function shedRoof(ctx, x, y, w, h, seed, color = C.tileDark) {
  const pts = [[x - 3, y + h], [x + w * 0.55, y - 4], [x + w + 3, y + h * 0.72], [x - 3, y + h * 0.72]];
  fillPoly(ctx, pts, color);
  spoly(ctx, pts, { color: PALETTE.ink, width: 1.3, seed });
  for (let i = 1; i < 5; i += 1) {
    const s = i / 5;
    tileLine(ctx, x + w * s, y + h * 0.72, x - 3 + (w * 0.55 + 3) * s, y - 4 + h * 0.04 * (1 - s), seed + i * 9);
  }
}

/** 草顶：乡下的房子 */
function thatchRoof(ctx, x, y, w, h, seed) {
  const pts = [[x - 4, y + h], [x + w / 2, y - 2], [x + w + 4, y + h]];
  fillPoly(ctx, pts, '#cbb273');
  spoly(ctx, pts, { color: PALETTE.ink, width: 1.3, seed });
  for (let i = 0; i < 8; i += 1) {
    const s = (i + 0.5) / 8;
    sline(ctx, x + w * s, y + h, x + w * s + wob(seed + i, 3), y + h * 0.45,
      { color: PALETTE.inkSoft, width: 0.9, alpha: 0.6, seed: seed + i });
  }
}

/** 一扇门：门洞 + 门缝 + 两个门环 */
function door(ctx, x, y, w, h, seed) {
  box(ctx, x, y, w, h, C.dark, seed, { width: 1.3 });
  sline(ctx, x + w / 2, y + 2, x + w / 2, y + h - 2, { color: PALETTE.ink, width: 0.9, alpha: 0.6, seed: seed + 2 });
  if (w > 14) {
    for (const dx of [w * 0.28, w * 0.72]) {
      sline(ctx, x + dx, y + h * 0.52, x + dx, y + h * 0.62, { color: PALETTE.gold, width: 1.4, seed: seed + dx });
    }
  }
}

/** 一扇窗：井字棂 */
function windowPane(ctx, x, y, w, h, seed, color = '#e7dcc2') {
  box(ctx, x, y, w, h, color, seed, { width: 1 });
  sline(ctx, x + w / 2, y, x + w / 2, y + h, { color: PALETTE.ink, width: 0.8, alpha: 0.55, seed: seed + 1 });
  sline(ctx, x, y + h / 2, x + w, y + h / 2, { color: PALETTE.ink, width: 0.8, alpha: 0.55, seed: seed + 2 });
}

/** 匾额：地名就写在这上头 */
function plaque(ctx, cx, y, w, h, text, seed) {
  box(ctx, cx - w / 2, y, w, h, C.paper, seed, { width: 1.2 });
  srect(ctx, cx - w / 2 + 1.5, y + 1.5, w - 3, h - 3, { color: C.timber, width: 0.9, seed: seed + 5, alpha: 0.8 });
  if (text) {
    stext(ctx, String(text).slice(0, 5), cx, y + h / 2 + 0.5,
      { font: `${Math.max(9, Math.round(h * 0.62))}px "LXGW WenKai Lite","KaiTi",serif` });
  }
}

/** 竖幡：挂在檐角的一块布 */
function banner(ctx, x, y, w, h, text, seed, color = C.cloth) {
  box(ctx, x, y, w, h, color, seed, { width: 1.1 });
  if (!text || h <= 22) return;
  ctx.save();
  ctx.fillStyle = PALETTE.paper;
  ctx.font = `${Math.min(11, Math.round(w * 0.62))}px "LXGW WenKai Lite","KaiTi",serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  const chars = String(text).slice(0, 3).split('');
  chars.forEach((ch, i) => {
    ctx.fillText(ch, x + w / 2, y + h * ((i + 1) / (chars.length + 1)));
  });
  ctx.restore();
}

/** 一排灯笼 */
function lanterns(ctx, x, y, w, n, seed) {
  for (let i = 0; i < n; i += 1) {
    const cx = x + w * ((i + 0.5) / n);
    sline(ctx, cx, y, cx, y + 4, { color: PALETTE.ink, width: 0.8, seed: seed + i });
    const pts = [[cx - 3.5, y + 4], [cx + 3.5, y + 4], [cx + 3, y + 11], [cx - 3, y + 11]];
    fillPoly(ctx, pts, '#e0704f');
    spoly(ctx, pts, { color: PALETTE.ink, width: 0.9, seed: seed + i * 3 });
  }
}

function steps(ctx, cx, y, w, seed) {
  for (let i = 0; i < 2; i += 1) {
    const ww = w + i * 6;
    sline(ctx, cx - ww / 2, y + i * 3, cx + ww / 2, y + i * 3, { color: PALETTE.ink, width: 1, seed: seed + i });
  }
}

function pillars(ctx, x, y, w, h, n, seed) {
  for (let i = 0; i < n; i += 1) {
    const s = n === 1 ? 0.5 : i / (n - 1);
    box(ctx, x + w * s - 2, y, 4, h, C.timber, seed + i * 7, { width: 1 });
  }
}

/* ------------------------------ 一座房子 ------------------------------ */

/**
 * 画一座房子。opt 决定长什么样：
 *  kind     shop/inn/yamen/garage/bath/fun/depot/junk/gate/home/temple/pet/library/clinic/carport/pawn
 *  name     写在匾额上的地名
 *  storeys  几层（两层才会有腰线与一排上窗）
 *  roof     gable 悬山 / stack 重檐 / shed 单坡 / thatch 草顶 / flat 平顶
 *  sign     plaque 匾额 | banner 幡子 | none
 */
export function drawBuilding(ctx, x, y, w, h, opt = {}) {
  const kind = opt.kind || 'shop';
  const name = opt.name || '';
  const storeys = opt.storeys || 1;
  const seed = (opt.seed || 0) + Math.round(x * 0.7 + y * 1.3);
  const cx = x + w / 2;
  const baseTop = y + h * 0.88;                       // 台基线
  const wallTop = y + h * (storeys > 1 ? 0.16 : 0.30);
  const openPlan = ['garage', 'junk', 'carport'];     // 这几种是大敞口，没有门扇

  // 地基
  fillPoly(ctx, [[x, baseTop], [x + w, baseTop], [x + w + 3, y + h], [x - 3, y + h]], C.brick);
  sline(ctx, x - 3, y + h, x + w + 3, y + h, { color: PALETTE.ink, width: 1.3, seed });

  // 墙
  box(ctx, x, wallTop, w, baseTop - wallTop, opt.wall || C.plaster, seed + 1);
  if (kind === 'yamen' || kind === 'temple') {
    hatch(ctx, x, wallTop, w, baseTop - wallTop, { gap: 11, seed: seed + 2, alpha: 0.18 });
  }

  // 两层：腰线 + 一排上窗
  if (storeys > 1) {
    const mid = y + h * 0.52;
    sline(ctx, x, mid, x + w, mid, { color: C.timber, width: 2, seed: seed + 3 });
    for (let i = 0; i < 3; i += 1) {
      windowPane(ctx, x + w * (0.14 + i * 0.28), wallTop + (mid - wallTop) * 0.22,
        w * 0.16, (mid - wallTop) * 0.42, seed + 30 + i);
    }
    lanterns(ctx, x + w * 0.12, y + h * 0.2, w * 0.76, 3, seed + 4);
  }

  // 门 / 窗
  if (!openPlan.includes(kind)) {
    const doorW = Math.max(10, Math.min(w * 0.3, 22));
    const doorH = baseTop - (storeys > 1 ? y + h * 0.52 : wallTop + h * 0.14);
    door(ctx, cx - doorW / 2, baseTop - doorH, doorW, doorH, seed + 8);
    steps(ctx, cx, baseTop, doorW + 8, seed + 9);
  }
  if (!openPlan.includes(kind) && kind !== 'fun') {
    const winW = w * 0.15;
    const winY = wallTop + h * (storeys > 1 ? 0.36 : 0.16);
    windowPane(ctx, x + w * 0.12, winY, winW, winW * 0.8, seed + 12);
    windowPane(ctx, x + w * 0.73, winY, winW, winW * 0.8, seed + 13);
  }

  // 屋顶：比格子高出去一点，才有压地的分量
  const roofH = h * 0.34;
  if (opt.roof === 'flat') {
    box(ctx, x - 4, wallTop - 4, w + 8, 4, C.tileDark, seed + 20, { width: 1.2 });
  } else if (opt.roof === 'shed') {
    shedRoof(ctx, x, wallTop - roofH * 0.5, w, roofH * 0.5, seed + 22, opt.roofColor);
  } else if (opt.roof === 'thatch') {
    thatchRoof(ctx, x, wallTop - roofH, w, roofH, seed + 23);
  } else if (opt.roof === 'stack') {
    stackRoof(ctx, cx, wallTop - roofH * 1.25, w / 2, seed + 24);
  } else {
    gableRoof(ctx, cx, wallTop - roofH, w / 2, wallTop, seed + 25, opt.roofColor || C.tile);
  }

  // 各家自己的零碎
  const deco = DECO[kind];
  if (deco) {
    deco(ctx, { x, y, w, h, cx, baseTop, wallTop, seed, name });
  }

  // 招牌地名
  if (opt.sign !== 'none' && name) {
    if (opt.sign === 'banner') {
      banner(ctx, x + w * 0.86, wallTop + h * 0.06, Math.min(14, w * 0.16), h * 0.34, name, seed + 40);
    } else {
      plaque(ctx, cx, wallTop + h * 0.02, Math.min(w * 0.62, 56), Math.max(13, h * 0.13), name, seed + 41);
    }
  }
}

/* ------------------------------ 各家的零碎 ------------------------------ */

const DECO = {
  /** 衙署：柱廊 + 石阶 + 门口两只石狮 */
  yamen(ctx, m) {
    pillars(ctx, m.x + m.w * 0.1, m.wallTop + m.h * 0.12, m.w * 0.8, m.baseTop - m.wallTop - m.h * 0.12, 4, m.seed);
    for (const dx of [m.w * 0.06, m.w * 0.94]) {
      const sx = m.x + dx;
      const pts = [[sx - 5, m.baseTop], [sx + 5, m.baseTop], [sx + 4, m.baseTop - 9], [sx - 4, m.baseTop - 9]];
      fillPoly(ctx, pts, PALETTE.stone);
      spoly(ctx, pts, { color: PALETTE.ink, width: 1, seed: m.seed + dx });
    }
    srect(ctx, m.x + m.w * 0.06, m.baseTop - 3, m.w * 0.88, 3, { color: PALETTE.stone, width: 1, seed: m.seed + 9 });
  },

  /** 庙观：柱廊 + 门口一只香炉，冒着烟 */
  temple(ctx, m) {
    pillars(ctx, m.x + m.w * 0.12, m.wallTop + m.h * 0.16, m.w * 0.76, m.baseTop - m.wallTop - m.h * 0.16, 4, m.seed);
    const ay = m.baseTop - 6;
    const pts = [[m.cx - 7, ay + 6], [m.cx + 7, ay + 6], [m.cx + 5, ay - 4], [m.cx - 5, ay - 4]];
    fillPoly(ctx, pts, C.brick);
    spoly(ctx, pts, { color: PALETTE.ink, width: 1.1, seed: m.seed });
    for (let i = 0; i < 2; i += 1) {
      sline(ctx, m.cx - 2 + i * 4, ay - 5, m.cx - 4 + i * 6, ay - 15,
        { color: PALETTE.inkSoft, width: 0.9, alpha: 0.5, seed: m.seed + i });
    }
  },

  /** 车铺：大敞口 + 门楣 + 墙上的轮胎 */
  garage(ctx, m) {
    box(ctx, m.x + m.w * 0.16, m.wallTop + m.h * 0.24, m.w * 0.68,
      m.baseTop - m.wallTop - m.h * 0.24, C.dark, m.seed + 3, { width: 1.3 });
    sline(ctx, m.x + m.w * 0.16, m.wallTop + m.h * 0.24, m.x + m.w * 0.84, m.wallTop + m.h * 0.24,
      { color: C.timber, width: 2, seed: m.seed + 4 });
    ctx.save();
    ctx.strokeStyle = PALETTE.ink;
    ctx.lineWidth = 1.2;
    ctx.beginPath();
    ctx.arc(m.x + m.w * 0.12, m.baseTop - 12, 5, 0, Math.PI * 2);
    ctx.stroke();
    ctx.restore();
  },

  /** 收破烂的院子：敞棚 + 几摞杂货 */
  junk(ctx, m) {
    box(ctx, m.x + m.w * 0.1, m.wallTop + m.h * 0.2, m.w * 0.8,
      m.baseTop - m.wallTop - m.h * 0.2, '#403a34', m.seed + 3, { width: 1.2 });
    for (let i = 0; i < 4; i += 1) {
      box(ctx, m.x + m.w * (0.16 + i * 0.19), m.baseTop - 11 - (i % 2) * 6, 9, 7,
        i % 2 ? '#9d8f76' : '#8a7d68', m.seed + i * 5, { width: 0.9 });
    }
  },

  /** 车棚：单坡顶 + 两根柱子 */
  carport(ctx, m) {
    pillars(ctx, m.x + m.w * 0.08, m.wallTop + m.h * 0.1, m.w * 0.84, m.baseTop - m.wallTop - m.h * 0.1, 2, m.seed);
    sline(ctx, m.x + m.w * 0.06, m.baseTop - 2, m.x + m.w * 0.94, m.baseTop - 2,
      { color: PALETTE.inkSoft, width: 1, alpha: 0.5, seed: m.seed + 6 });
  },

  /** 汤池：矮墙圈出的热气 */
  bath(ctx, m) {
    for (let i = 0; i < 3; i += 1) {
      const sx = m.cx - 12 + i * 12;
      ctx.save();
      ctx.strokeStyle = PALETTE.inkSoft;
      ctx.lineWidth = 1;
      ctx.globalAlpha = 0.4;
      ctx.beginPath();
      for (let t = 0; t <= 10; t += 1) {
        const xx = sx + Math.sin(t * 0.9 + i) * 3;
        if (t === 0) ctx.moveTo(xx, m.wallTop); else ctx.lineTo(xx, m.wallTop - t * 2.2);
      }
      ctx.stroke();
      ctx.restore();
    }
    srect(ctx, m.x + m.w * 0.18, m.baseTop - 8, m.w * 0.64, 8, { color: PALETTE.water, width: 1.2, seed: m.seed + 2 });
  },

  /** 瓦舍：挂着幔子的戏台 */
  fun(ctx, m) {
    const top = m.wallTop + m.h * 0.06;
    const hgt = m.h * 0.5;
    fillPoly(ctx, [[m.x + 2, top], [m.x + m.w - 2, top], [m.x + m.w - 2, top + hgt], [m.x + 2, top + hgt]], C.clothBlue);
    ctx.save();
    ctx.strokeStyle = PALETTE.ink;
    ctx.lineWidth = 1.2;
    ctx.beginPath();
    ctx.moveTo(m.x + 2, top);
    ctx.lineTo(m.x + 2, top + hgt);
    for (let i = 0; i <= 6; i += 1) {
      ctx.lineTo(m.x + 2 + (m.w - 4) * (i / 6), top + hgt + Math.sin(i * 1.6) * 3);
    }
    ctx.lineTo(m.x + m.w - 2, top);
    ctx.stroke();
    ctx.restore();
    banner(ctx, m.x + 3, m.wallTop + m.h * 0.1, 11, m.h * 0.3, '戏', m.seed + 8, C.cloth);
  },

  /** 货栈：双扇大门 + 门前的货箱 */
  depot(ctx, m) {
    const dw = m.w * 0.44;
    const dh = m.h * 0.4;
    const dx = m.cx - dw / 2;
    const dy = m.baseTop - dh;
    box(ctx, dx, dy, dw, dh, C.dark, m.seed + 3, { width: 1.3 });
    srect(ctx, dx + 1, dy + 1, dw / 2 - 1, dh - 2, { color: C.timber, width: 0.9, seed: m.seed + 4, alpha: 0.8 });
    srect(ctx, dx + dw / 2, dy + 1, dw / 2 - 1, dh - 2, { color: C.timber, width: 0.9, seed: m.seed + 5, alpha: 0.8 });
    for (let i = 0; i < 3; i += 1) {
      box(ctx, m.x + 4 + i * 11, m.baseTop - 8 - (i % 2) * 6, 10, 8, '#a89070', m.seed + i * 7, { width: 0.9 });
    }
  },

  /** 当铺：高墙小窗 + 一只大「当」幌 */
  pawn(ctx, m) {
    windowPane(ctx, m.x + m.w * 0.2, m.wallTop + m.h * 0.1, m.w * 0.2, m.w * 0.16, m.seed + 3, '#efe6cf');
    banner(ctx, m.x + m.w * 0.72, m.wallTop + m.h * 0.08, 13, m.h * 0.42, '当', m.seed + 4, '#e6dcc4');
  },

  /** 牲口市：木栏 + 一地干草 */
  pet(ctx, m) {
    const top = m.baseTop - m.h * 0.16;
    for (let i = 0; i < 6; i += 1) {
      sline(ctx, m.x + m.w * 0.08 + i * (m.w * 0.14), top, m.x + m.w * 0.08 + i * (m.w * 0.14), m.baseTop,
        { color: C.timber, width: 1.2, seed: m.seed + i });
    }
    sline(ctx, m.x + m.w * 0.06, top + 4, m.x + m.w * 0.94, top + 4, { color: C.timber, width: 1.4, seed: m.seed + 9 });
    hatch(ctx, m.x + m.w * 0.1, m.baseTop - 6, m.w * 0.8, 6, { gap: 6, seed: m.seed + 11, alpha: 0.3, color: '#c9b273' });
  },

  /** 书局 / 药铺：檐角挂一只字幌 */
  library(ctx, m) {
    banner(ctx, m.x + m.w * 0.72, m.wallTop + m.h * 0.08, 12, m.h * 0.36, '书', m.seed + 3, '#e9e2d0');
  },
  clinic(ctx, m) {
    banner(ctx, m.x + m.w * 0.72, m.wallTop + m.h * 0.08, 12, m.h * 0.36, '药', m.seed + 3, '#d9e0d0');
  },
  eatery(ctx, m) {
    banner(ctx, m.x + m.w * 0.72, m.wallTop + m.h * 0.08, 12, m.h * 0.34, '食', m.seed + 3, '#e2cfa8');
  },

  /** 大车店：檐下两只灯笼 */
  inn(ctx, m) {
    lanterns(ctx, m.x + m.w * 0.1, m.baseTop - m.h * 0.1, m.w * 0.8, 2, m.seed + 3);
  },

  /** 民居：柴垛 */
  home(ctx, m) {
    box(ctx, m.x + 3, m.baseTop - 9, 12, 9, '#b39a6f', m.seed + 3, { width: 1 });
    sline(ctx, m.x + 3, m.baseTop - 6, m.x + 15, m.baseTop - 6,
      { color: PALETTE.inkSoft, width: 0.8, alpha: 0.6, seed: m.seed + 4 });
  },

  /** 城楼关口：石台 + 拱门 */
  gate(ctx, m) {
    box(ctx, m.x + m.w * 0.22, m.baseTop - m.h * 0.5, m.w * 0.56, m.h * 0.5, '#3c3730', m.seed + 3, { width: 1.3 });
    ctx.save();
    ctx.strokeStyle = PALETTE.ink;
    ctx.lineWidth = 1.3;
    ctx.beginPath();
    ctx.arc(m.cx, m.baseTop - m.h * 0.16, m.w * 0.28, Math.PI, 0);
    ctx.stroke();
    ctx.restore();
    hatch(ctx, m.x, m.wallTop, m.w, m.baseTop - m.wallTop, { gap: 9, seed: m.seed + 5, alpha: 0.25 });
  },
};

/* ------------------------------ 器物认门牌 ------------------------------ */

/** 每种地点长什么样（place_props.js 与门口面板共用） */
export const BUILDING_PRESETS = {
  shop: { storeys: 1, wall: C.plaster, roof: 'gable', sign: 'plaque' },
  general: { storeys: 1, wall: C.plaster, roof: 'gable', sign: 'plaque' },
  eatery: { storeys: 1, wall: C.plaster, roof: 'gable', sign: 'plaque' },
  inn: { storeys: 2, wall: C.brick, roof: 'gable', sign: 'plaque' },
  pawn: { storeys: 1, wall: C.brick, roof: 'gable', sign: 'plaque' },
  garage: { storeys: 1, wall: '#c8bfae', roof: 'shed', sign: 'plaque' },
  carport: { storeys: 1, wall: '#c8bfae', roof: 'shed', sign: 'plaque' },
  junk: { storeys: 1, wall: '#b6a68d', roof: 'shed', sign: 'plaque' },
  depot: { storeys: 1, wall: C.brick, roof: 'gable', sign: 'plaque' },
  fun: { storeys: 1, wall: C.plaster, roof: 'gable', sign: 'banner' },
  bath: { storeys: 1, wall: C.plaster, roof: 'flat', sign: 'plaque' },
  library: { storeys: 1, wall: C.plaster, roof: 'gable', sign: 'plaque' },
  clinic: { storeys: 1, wall: C.plaster, roof: 'gable', sign: 'plaque' },
  pet: { storeys: 1, wall: C.plaster, roof: 'shed', sign: 'plaque' },
  yamen: { storeys: 1, wall: '#ddd2bb', roof: 'stack', sign: 'plaque' },
  temple: { storeys: 1, wall: '#e8dfcb', roof: 'stack', sign: 'plaque' },
  gate: { storeys: 1, wall: C.brick, roof: 'stack', sign: 'plaque' },
  home: { storeys: 1, wall: C.earth, roof: 'thatch', sign: 'plaque' },
};

export const PLACE_TYPES = Object.keys(BUILDING_PRESETS);

/** 给场景里的「地点建筑」器物用：state.name 就是写在匾上的地名 */
export function drawPlace(ctx, x, y, w, h, p) {
  const st = p.state || {};
  const kind = st.kind || String(p.type || 'place_shop').replace(/^place_/, '');
  const preset = BUILDING_PRESETS[kind] || BUILDING_PRESETS.shop;
  drawBuilding(ctx, x, y, w, h, {
    ...preset,
    kind,
    name: st.name || (p.def && p.def.name) || '',
    seed: (st.seed || 0) + (p.gx || 0) * 31 + (p.gy || 0) * 17,
  });
}

/** 把画法挂到 PROP_ART 上，各家器物一个入口 */
export function installBuildingArt() {
  for (const kind of PLACE_TYPES) {
    PROP_ART[`place_${kind}`] = drawPlace;
  }
}
